import React, { useEffect } from 'react';
import {
	OverlayModalComponentProps,
	OverlayModalProps,
} from '@sharefiledev/sharefile-appshell';
import { Button, Flex, Spin } from 'antd';
import antdTheme from 'antd/es/theme';
import { openWOPILink } from '../../Addins/addin';
import { useAppStore } from '../../common/store';
import { DocGenExtensionSlots, WorkflowType } from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { handleErrorWithServerMessage } from '../../data/errorHandler';
import { DocumentTemplate } from '../../data/interfaces/DocumentTemplate';
import { CopyFileFromSharefilePayload } from '../../data/interfaces/DocumentTemplateFile';
import { ProgressState } from '../../pages/Start/SelectDocument/types';
import { UploadPreview } from '../../pages/Start/UploadPreview';
import { t } from '../../utils';
import { FileUploadComponent } from '../FileUploadComponent';

export type UploadDocxFileModalProps = {
	cancelInteraction: OverlayModalComponentProps<
		typeof DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL
	>['cancelInteraction'];

	completeInteraction: OverlayModalComponentProps<
		typeof DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL
	>['completeInteraction'];
};

export const UploadDocxFileModal = ({
	cancelInteraction,
	completeInteraction,
}: UploadDocxFileModalProps) => {
	const [uploadState, setUploadState] = React.useState<ProgressState>('');
	const [isRemoveFileDisabled, setIsRemoveFileDisabled] = React.useState(false);

	const {
		resetUrlSliceData,
		templateMetadata,
		updateDocumentTemplate,
		documentTemplate,
		fetchThumbnailUrl,
		updateTemplateMetadata,
	} = useAppStore(state => ({
		resetUrlSliceData: state.resetUrlSliceData,
		templateMetadata: state.templateMetadata,

		updateDocumentTemplate: state.updateDocumentTemplate,
		documentTemplate: state.documentTemplate,
		fetchThumbnailUrl: state.fetchThumbnailUrl,
		updateTemplateMetadata: state.updateTemplateMetadata,
	}));

	useEffect(() => {
		const onMount = async () => {
			if (
				templateMetadata?.workflow === WorkflowType.ContentViewer ||
				templateMetadata?.workflow === WorkflowType.ContextMenu
			) {
				try {
					setUploadState('started');
					const payload: CopyFileFromSharefilePayload = {
						fileName: templateMetadata?.selectedItem?.FileName,
						fileId: templateMetadata?.selectedItem?.Id,
						overwrite: true,
					};
					const response = await DocgenClient.copyFileFromSharefile(
						documentTemplate?.rid,
						payload
					);

					if (response !== null) {
						setUploadState('storing');
						updateDocumentTemplate({
							file: {
								...documentTemplate?.file,
								fileName: templateMetadata?.selectedItem?.FileName,
							},
						});
						await fetchThumbnailUrl(documentTemplate?.rid);
						setUploadState('ready');
					}
				} catch (_) {
					handleErrorWithServerMessage(t('errorMessages.genericError'));
				}
			}
		};
		if (documentTemplate?.rid) {
			onMount();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentTemplate?.rid]);

	function resetUploadState() {
		setUploadState('');
		resetUrlSliceData();
		updateTemplateMetadata(null);
		updateDocumentTemplate({ file: null });
	}

	const resetUpload = () => {
		resetUploadState();
		deleteUploadedFile(documentTemplate?.rid);
	};

	return (
		<>
			{!documentTemplate?.rid ? (
				<Spin size="large" />
			) : uploadState ? (
				<Flex justify="center">
					<UploadPreview
						uploadState={uploadState}
						resetUpload={resetUpload}
						isRemoveFileDisabled={isRemoveFileDisabled}
						setIsRemoveFileDisabled={setIsRemoveFileDisabled}
					/>
				</Flex>
			) : (
				<FileUploadComponent
					setUploadState={setUploadState}
					resetUploadState={resetUploadState}
				/>
			)}
			<UploaderDocxFileModalFooter
				onCancel={cancelInteraction}
				documentTemplate={documentTemplate}
				onSuccess={completeInteraction}
			/>
		</>
	);
};

const deleteUploadedFile = async (rid: DocumentTemplate['rid']) => {
	try {
		await DocgenClient.deleteUploadedFile(rid);
	} catch {
		handleErrorWithServerMessage(t('errorMessages.deleteUploadedFileError'));
	}
};

export const onUploadDocxFileModalCancel = async () => {
	const { resetStore, documentTemplate } = useAppStore.getState();
	if (documentTemplate?.rid) {
		DocgenClient.deleteTemplate(documentTemplate?.rid);
		resetStore();
	}
};

export const getUploadDocxFileModalProps = (
	overrideOverlayModalProps?: OverlayModalProps
) => {
	const { getDesignToken } = antdTheme;
	const token = getDesignToken();
	const overlayModalProps = {
		title: t('uploadDocxFileModal.title'),
		onCancel: onUploadDocxFileModalCancel,
		centered: true,
		width: 840,
		footer: null,
		styles: {
			body: {
				marginTop: token.marginMD,
			},
		},
		...overrideOverlayModalProps,
	};

	return overlayModalProps;
};

type UploaderDocxFileModalFooterProps = {
	onCancel: () => void;
	documentTemplate: DocumentTemplate;
	onSuccess: () => void;
};

const UploaderDocxFileModalFooter = ({
	onCancel,
	documentTemplate,
	onSuccess,
}: UploaderDocxFileModalFooterProps) => {
	return (
		<Flex>
			{documentTemplate?.rid && <CreateButton onSuccess={onSuccess} />}
			<Button
				onClick={() => {
					onCancel();
				}}
			>
				{t('cancel')}
			</Button>
		</Flex>
	);
};

type CreateButtonProps = {
	onSuccess: () => void;
};

const CreateButton = ({ onSuccess }: CreateButtonProps) => {
	const { documentTemplate, isLoading, setIsLoading } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		isLoading: state.isLoading,
		setIsLoading: state.setIsLoading,
	}));

	const onClick = async () => {
		setIsLoading(true);
		await openWOPILink(documentTemplate);

		onSuccess();
	};

	return (
		<Button
			type="primary"
			onClick={onClick}
			disabled={!documentTemplate?.file?.fileName}
			loading={isLoading}
		>
			{t('uploadDocxFileModal.okButtonText')}
		</Button>
	);
};

export default UploadDocxFileModal;
